// Turn flow: firing, the flight simulation, hit detection and scoring.

import { W, H } from './canvas.js';
import { state } from './state.js';
import { clamp, rand } from './utils.js';
import {
  BOOMERANG_CATCH_R,
  BOOMERANG_DRAG,
  BOOMERANG_HOME_ARC_FRAMES,
  BOOMERANG_HOME_FRAMES,
  BOOMERANG_HOME_LIFT,
  BOOMERANG_HOME_SLOPE,
  BOOMERANG_OUT_FRAMES,
  BOOMERANG_PULL,
  BOOMERANG_RETURN_GRAVITY,
  BOOMERANG_SPIN,
  CHARGE_CAP,
  CRATER_JITTER,
  CRATER_RADIUS,
  CPU_GUARD_CHANCE,
  CPU_LEVELS,
  CPU_SOLVE_FRAMES,
  CPU_ULT_CHANCE,
  DRONE_BLAST_R,
  DRONE_BOMB_CRATER,
  DRONE_BOMB_VY,
  DRONE_DROP_X,
  DRONE_SPEED,
  DRONE_Y,
  GRENADE_BLAST_R,
  GRENADE_BOUNCE,
  GRENADE_CRATER_SCALE,
  GRENADE_FUSE_FRAMES,
  GRENADE_REST_SPEED,
  GRENADE_SETTLE_FUSE,
  BALLS,
  HIT_SWEEP_PX,
  LEAP_FLIGHT_FRAMES,
  LEAP_SCATTER,
  ROUND_DELAY_MS,
  SELF_HIT_GRACE_FRAMES,
  SHEEP_CRATER_SCALE,
  SHEEP_FUSE_MS,
  SHEEP_HOP_VY,
  SHEEP_MARGIN,
  SHEEP_SPEED,
  SHEEP_STEP_UP,
  SPEED_SCALE,
  SPIN,
  SPRING_KICK,
  TURN_RUSH_SECONDS,
  UAV_PAINT_MS,
  WIND_DRIFT,
} from './config.js';
import { angleSlider, closePanels, fireBtn, humanSide, linkPanel, loadAim, machineHasTurn, openPanel, playerName, powerSlider, prepareSide, saveAim, secondsLeft, shareBtn, shareCode, shareMsg, showToast, updateLabels, updateSpecialButton, updateTurnClock } from './dom.js';
import { moonAt } from './moon.js';
import { endSplashScene } from './splash.js';
import { collectCrate, crateRect } from './crates.js';
import { makeCity } from './city.js';
import { crater, explode, nuke } from './effects.js';
import { facingOf, gorillaFistPos, gorillaHead, gorillaShieldRect, gorillaSpringRect, hitsGorilla } from './render/gorilla.js';
import { LEAP_RELEASE_FRAME, endLeap, leapDone, startLeap } from './leap.js';
import { initAudio, playBaa, playClink, playDrone, playGrenade, playReward, playThrow, playTimeUp, playTurnTick, playVictory } from './audio.js';
import { hush, say } from './banter.js';
import { t } from './i18n.js';
import { wakeGorillas } from './antics.js';
import { activeMode, applyMode, isClassicMode, mods } from './modes.js';
import { randomSeed } from './rng.js';
import { savedCpuLevel } from './progress.js';
import { publish } from './leaderboard.js';
import { renderResults } from './dom.js';
import { matchOnHold, showScreen } from './screens.js';
import { resetWeather } from './weather.js';
import { SPECIALS, addResult, adoptRivalLook, dealCpuKit, myLook, record, resetSpecialUses, restoreLooks, savedMode, savedName, savedTargetWins, savedTurnSeconds, specialsOfKind } from './progress.js';
import { applyMatch, decodeMatch, encodeMatch } from './matchcode.js';
import { sendShot } from './net.js';

/**
 * Hands this machine's version of the match to the other one.
 *
 * The code is the whole state — city, round, whose turn it is, the score, the craters — so
 * there is no delta to get wrong and nothing to re-simulate. The machine that resolved the
 * shot owns the result and the other one takes it as read; that is also the only way it can
 * work, because the two gorillas' antics move their hitboxes, so replaying a shot from its
 * angles would not land in the same place twice.
 *
 * `over` travels beside the code rather than inside it. A match code is a match to carry on
 * with — applyMatch forgets that a match can be finished, on purpose, because carrying one on
 * is what a code pasted into a chat window is for — and a duel over a wire needs the other
 * end to know when to stop playing.
 */
export function syncOnline() {
  if (!state.online) return;
  sendShot(JSON.stringify({ code: encodeMatch(), over: !!state.matchOver, look: myLook() }));
}

/**
 * A turn's clock, from now.
 *
 * Three places give the clock a turn to count — a turn passed, the first turn of a duel, and the
 * moment the other side of a room turns up — and the hold is the part that is easy to forget: a
 * marker left up by whatever screen was showing before is *earlier* than the turn it would be
 * credited to, so leaving it in place hands the turn time it never had. Measured: a fifteen
 * second turn read 21.3 seconds because the menu's hold was still up when the duel began.
 */
export function startTurnClock() {
  state.turnStartedAt = state.clock;
  state.turnHoldSince = 0;
  // Y el tic de aviso empieza de cero con el turno: si no, un turno nuevo que nace ya dentro del
  // tramo de aviso se quedaría sin su primer tic —o lo daría con el número del anterior—.
  state.turnTickAt = 0;
}

/**
 * Says who this side is, before anybody has shot.
 *
 * The first code of a match carries the look with it, which is enough for whichever side
 * answered the pool. The side that was waiting would otherwise be drawing the other player out
 * of its own save file until they took their first turn, which is a whole turn of aiming at a
 * target of the wrong size. So both ends say it the moment they are matched.
 */
export function sendLook() {
  if (!state.online) return;
  sendShot(JSON.stringify({ look: myLook() }));
}

/**
 * Hands the turn over by hand: the code goes up and the board waits behind it, paused.
 *
 * A duel by correspondence has no wire to hand it over on — the code IS the handover, and it is
 * the player who carries it. So it is put in front of them the moment it exists, which is when
 * the turn leaves their hands, and the match is paused while it travels: nobody is going to
 * shoot while their turn is in somebody else's pocket, and a turn clock would be counting down
 * on a turn that cannot be taken.
 */
export function showHandover() {
  shareCode.value = encodeMatch();
  shareMsg.textContent = t('share.waiting');
  showScreen('pause');
  openPanel(linkPanel);
}

/**
 * Puts an invitation in the box and takes the button away: there is nothing left to generate,
 * because the code is already there and it is the first and only one of the duel.
 *
 * The invitation is the four-four code — the short one, the one that is read out loud and pasted
 * on the front page. The long codes that carry a match in progress are a different shape for a
 * different job, and they arrive here on their own, when the turn goes.
 */
export function showInviteCode(code) {
  shareCode.value = code;
  shareMsg.textContent = t('share.invite');
  if (shareBtn) shareBtn.hidden = true;
}

/**
 * The invitation: the first code of a duel, and the one meant to be heard rather than pasted.
 *
 * A code of its own because the four-four one says everything a duel nobody has played yet has
 * to say, and the long ones that follow say everything else. The generate button goes: there is
 * nothing to generate, the code is already in the box — and the player can read it out.
 */
export function showInvite(code) {
  showInviteCode(code);
  showScreen('pause');
  openPanel(linkPanel);
}

/**
 * The rival's shot: their state, applied as it stands.
 *
 * Nothing is counted up here. The wins, the round and the turn all arrive in the code,
 * because only the machine that resolved the death knows what it cost — so this one takes
 * the score as read and, if the code says the match is over, shows the card it already has
 * the numbers for. The alternative, both machines ruling on a death, is two machines
 * disagreeing about a match they are supposed to be sharing.
 */
export function receiveShot(payload) {
  if (!state.online) return;

  let over = false;
  let data = null;

  try {
    const envelope = JSON.parse(String(payload || ''));
    over = !!envelope.over;
    // Somebody's character, which arrives on its own as well as beside a code: it is not part
    // of a match, it goes on the side that sent it — always the other one — and a message that
    // carries nothing else is done here.
    adoptRivalLook(envelope.look);
    if (!envelope.code) return;
    data = decodeMatch(envelope.code);
  } catch {
    // A code that cannot be read is not applied halfway: the turn stays where it is and the
    // other end is left to send the next one.
    return;
  }

  applyMatch(data);

  // Y el dron del que escribió el código, si lo tenía en el aire: vuela hacia el lado que ahora
  // tiene el turno, que es al que iba pintando. El daño no se simula aquí —el resultado ya viene
  // en el marcador del propio código, como con cualquier otro tiro—, pero sin esto el de enfrente
  // se comía una ronda sin haber visto pasar nada.
  if (data.dr === 1 && !state.drone) {
    const from = 1 - state.turn;
    state.drone = { x: from === 0 ? -46 : W + 46, y: DRONE_Y, dir: from === 0 ? 1 : -1 };
    state.paintedFor = 1 - from;
    state.paintedUntil = state.clock + UAV_PAINT_MS;
  }

  closePanels();
  updateLabels();

  if (over) {
    state.matchOver = true;
    state.matchLive = false;
    fireBtn.disabled = true;
    restoreLooks();
    renderResults();
    showScreen('results');
    return;
  }

  // The first code of a random match brings the game forward as well: whoever answered the
  // pool never started a match of their own.
  showScreen('game');
}

/** Cancels a queued CPU shot (e.g. when the CPU is switched off mid-think). */
export function cancelCpuTurn() {
  clearTimeout(state.cpuTimer);
  state.cpuTimer = null;
}

/** Drops pending timers so a reset cannot inherit a CPU shot or a round change. */
function clearPendingTimers() {
  cancelCpuTurn();
  clearTimeout(state.roundTimer);
  state.roundTimer = null;
}

/** Starts a brand new match: city, scores and round counter all reset. */
export function newMatch() {
  clearPendingTimers();
  // El modo guardado se aplica aquí, al empezar, y no en la tarjeta del menú: elegirlo con una
  // partida en curso cambiaba los cráteres y la gravedad en el mismo turno —medido, el cráter de
  // 1 a 3,2 y la gravedad de 0,072 a 0,0238— mientras el reloj y las rondas no se movían. Un
  // ajuste de partida vale para la partida que empieza, no para la que se está jugando: la
  // tarjeta se marca sola (renderSetup) y esto es lo que la pone en vigor.
  //
  // Y no pisa el modo de un código: `applyInvite` y `applyMatch` lo aplican después de esto, que
  // es el orden correcto — la partida de otra máquina manda sobre la preferencia local.
  applyMode(savedMode());
  state.matchWins = [0, 0];
  state.score = [0, 0];
  state.round = 1;
  state.matchOver = false;
  // From here there is a match to come back to, and the menu says so.
  state.matchLive = true;
  // The city puts both gorillas back on their perches; this only tells the opening
  // scene to let go of them.
  endSplashScene();
  // A fresh seed per match, so no two matches are the same city — and so the
  // code that shares this one can rebuild it exactly.
  state.matchSeed = randomSeed();
  // Specials are per match, not per round, and each side carries its own bag.
  // The player's comes from the awards; the CPU's is dealt, because it has no
  // awards to draw on.
  state.special = [null, null];
  state.guard = [null, null];
  state.specialUses = [resetSpecialUses(), dealCpuKit()];

  // A new match starts with an empty sky. Nothing the last one had in the air
  // should still be crossing when this one begins — and a screenful of meteors
  // is the exact thing CLASSIC exists to avoid.
  state.meteors = [];
  state.iss = null;
  state.rocket = null;
  state.banner = null;
  state.airforce = null;
  state.nextMeteor = 0;
  state.nextIss = 0;
  state.nextRocket = 0;
  state.nextBanner = 0;
  state.nextAirforce = 0;
  // Nothing the last match had in the air, and nobody still talking.
  state.drone = null;
  // Y su bomba, que es lo mismo: una carga heredada del partido anterior estallando en la
  // primera ronda de este.
  state.bomb = null;
  // Las vidas extra son de la partida, así que una partida nueva empieza sin ninguna.
  state.lives = [0, 0];
  state.paintedFor = -1;
  state.paintedUntil = 0;
  state.wasted = null;
  hush();
  resetWeather();

  fireBtn.disabled = false;
  // A new match has had nothing spent in it yet. See state.js.
  state.spentInMatch = false;
  // And it is played to the length the setup is offering: taken here, once, so that changing the
  // setting while this match is on cannot move its finish line.
  state.targetWins = savedTargetWins();
  // Y el reloj, por lo mismo que las rondas: es una regla de la partida y no una preferencia, así
  // que se copia aquí una vez y deja de seguir al ajuste. Sin esto, `secondsLeft` preguntaba al
  // ajuste guardado en cada fotograma y tocar el reloj en AJUSTES lo movía en la partida en curso.
  state.turnSeconds = savedTurnSeconds();
  makeCity();
}

/** Hands the turn over to the other side after a miss. */
function passTurn() {
  state.turn = 1 - state.turn;
  startTurnClock();
  // The equipment menu belongs to the turn that has just ended. With the clock now
  // running while it is open, it can be on screen at the moment the turn is lost,
  // and a menu for choosing a throw has no business hanging over somebody else's
  // turn — least of all the CPU's, which would play out behind it.
  closePanels();
  fireBtn.disabled = false;
  // A shield lasts one enemy shot, and a shot has just been resolved — so whatever
  // happened to it, its turn is over. The mushroom is not here on purpose: it waits
  // for the shot it saves him from, and a miss is not one.
  //
  // Y el escudo que se gasta aquí es el del lado que ACABA DE DEFENDER, que con el turno ya
  // cambiado es `state.turn`; el del que acaba de tirar se queda. Estaba al revés: se borraba
  // el de quien entregaba el turno, o sea el escudo recién levantado, antes de que el rival
  // hubiera tirado — así que el escudo no paraba nada nunca. Como levantar la defensa y tirar
  // son excluyentes (`brace` deja el botón de disparo deshabilitado), el escudo que sobrevive
  // aquí es el que estaba esperando el tiro del rival, que es exactamente lo que promete.
  //
  // And neither is the spring. It is a thing standing on the roof, not a turn: it waits for a
  // banana to land on it and it is spent when one does, which is where it is cleared. Clearing
  // it here as well meant it was gone before the shot it was planted for had been thrown — and
  // the turn after planting it is a turn spent AIMING, with his own spring sitting under him
  // doing nothing.
  if (state.shield && state.shield.idx === state.turn) state.shield = null;
  // Back to the aim of whoever is sitting at THIS keyboard, rather than whatever the other one
  // left up there — and not to the turn's own side, which is the same person against the machine
  // and in a game of two people, and nobody at all over a wire: putting the rival's angle and
  // power on the sliders of a machine that cannot play them is the controls changing hands to
  // nobody. `prepareSide()` is the side they belong to here; see dom.js for the three modes.
  loadAim(prepareSide());
  updateLabels();
  // And over a wire the turn is not only handed over, it is sent: the machine on the other
  // end is the only one that can play it, and this state is what it has to play against.
  if (state.online) {
    if (state.byCode) {
      showHandover();
    } else {
      syncOnline();
    }
  }
  // And the turn is armed here rather than by whoever asked for it. Six callers used
  // to do it themselves, every one of them after their own call to this function, and
  // they all did it right -- which is exactly what made the seventh forget. The seventh
  // is the turn clock: a turn lost to time comes through passTurn(true) from the frame
  // loop, alone, and a solo match then sat on the CPU's turn with the clock at zero and
  // nothing to fire it. One place hands a turn over, and that is the place that arms it.
  maybeCpuTurn();
}

/**
/**
 * Braced instead of thrown: the defence goes up and the turn is spent on it.
 *
 * It used to be three branches inside fireShot, because a defence was the thing in the
 * one slot and pressing fire spent whatever was in it. With a slot each, an attack and a
 * defence can be held at once — which is the point of the two — and then one button
 * cannot be both. So bracing has its own, and the price is unchanged: the shot you did
 * not take.
 */
export function brace() {
  if (state.projectile || state.leap || state.sheep || state.matchOver) return;

  const id = mods().specials ? state.guard[state.turn] : null;
  const special = id ? SPECIALS[id] : null;
  if (!special || !special.guard) return;

  spendSpecial(id);
  record('guard');
  fireBtn.disabled = true;

  if (special.shield) {
    state.shield = { idx: state.turn, at: state.clock };
  } else if (special.spring) {
    state.spring = { idx: state.turn, at: state.clock };
  } else {
    state.mushroom = { idx: state.turn, at: state.clock };
  }

  initAudio();
  wakeGorillas();

  // Handed over by hand rather than through passTurn, because passTurn clears the
  // shield and the spring: this is the one turn change they have to survive.
  state.turn = 1 - state.turn;
  state.turnStartedAt = state.clock;
  // Same rule as passTurn: the sliders go back to this keyboard's side, not to the side that
  // just spent its turn bracing.
  loadAim(prepareSide());
  updateLabels();
  // Y el botón vuelve, que es lo que este camino hacía de menos.
  //
  // El `disabled = true` de arriba es para quien acaba de gastar su turno braceando — y **el que
  // bracea no siempre es el de esta máquina**. Cuando la defensa la levanta la CPU, ese botón es el
  // del jugador: se quedaba deshabilitado con el turno ya en su mano, y nadie lo rehabilitaba hasta
  // que el reloj entregaba el turno por su cuenta, quince segundos después. Reportado así: «cuando
  // el otro jugador saca el muelle, el botón de lanzar me desaparece y no puedo hacer nada»,
  // contra la máquina en MODERN. Medido: la CPU bracea, `state.turn` pasa a 0 y `#fire` sigue
  // `disabled` diciendo THROW.
  //
  // Se pone a `false` sin condiciones, igual que en `passTurn`, y es correcto en los dos casos:
  // cuando el turno es del otro lado, el botón no se distingue por `disabled` sino por la clase
  // `.away` —que es la que lo aparta y lo deja diciendo que se espera—, y el tiro lo corta
  // `machineHasTurn()` en `input.js` antes de mirar el botón. `updateLabels()` no toca `disabled`,
  // que es por lo que hay que decirlo aquí.
  fireBtn.disabled = false;
  // And the other end is told, which is what makes a defence worth anything over a wire. The
  // resolution of a shot happens on the machine that threw it, so a defence the thrower has
  // not been told about is a defence that is not there: it has to travel, and this is the code
  // that carries it. passTurn says this for a turn thrown away; a brace is a turn thrown away
  // by hand (see above), so it has to say it for itself.
  if (state.online) {
    if (state.byCode) {
      showHandover();
    } else {
      syncOnline();
    }
  }
  maybeCpuTurn();
}

/**
 * Launches a banana from the current player's fist.
 *
 * One function for both sides. The CPU's shot used to be marked `auto` so that it
 * could be refused the player's special; now that each side carries its own bag
 * there is nothing to refuse, and the flag went with it.
 */
export function fireShot() {
  if (state.projectile || state.sheep || state.matchOver) return;
  // A leap is in the air even though its banana has not left the hand yet, and
  // pressing fire again during one would drop the first jump on the floor.
  if (state.leap) return;

  const p = state.players[state.turn];
  if (!p || !p.alive) return;

  // His own angle and power, filed before anything can move them. The sliders are
  // one pair shared by both sides and the CPU sets them to its own numbers when it
  // fires, so this is the moment a side's aim is still on them.
  saveAim(state.turn);

  // The thrower's own selection, whoever is throwing. This used to be refused to
  // the CPU outright, which is why it never used a special in its life; now that
  // each side has its own slot there is nothing to refuse — a shot can only ever
  // spend the charges of the gorilla throwing it.
  const special = mods().specials && state.special[state.turn]
    ? SPECIALS[state.special[state.turn]]
    : null;

  if (special && special.sheep) {
    launchSheep(state.turn);
    return;
  }

  // A leap is a different shot entirely: nothing leaves the hand yet. He jumps
  // first and throws from the top of it, at whatever the rival's head was doing
  // the moment fire was pressed — read once, here, so the jump itself counts
  // towards the time the rival has to not be there.
  if (special && special.leap) {
    const rival = state.players[1 - state.turn];
    if (!rival || !rival.alive) return;

    spendSpecial(state.special[state.turn]);
    fireBtn.disabled = true;
    startLeap(state.turn, gorillaHead(rival, 1 - state.turn));
    initAudio();
    wakeGorillas();
    return;
  }

  // The defences are not here any more: bracing is an action of its own, with a button
  // of its own, and it throws nothing. See brace().

  const dir = facingOf(state.turn);
  const a = Number(angleSlider.value) * Math.PI / 180;
  const speed = Number(powerSlider.value) * SPEED_SCALE;
  const start = gorillaFistPos(p, state.turn);
  const grenade = !!(special && special.grenade);
  const boomerang = !!(special && special.boomerang);
  // A ball is an ordinary throw of an unordinary thing: it goes through all of the
  // same machinery and only the numbers and the picture change. Which ball is
  // settled here, so the player finds out at the same moment the gorilla does.
  const ball = special && special.ball ? BALLS[Math.floor(Math.random() * BALLS.length)] : null;

  state.projectile = {
    x: start.x,
    y: start.y,
    vx: Math.cos(a) * speed * dir,
    vy: -Math.sin(a) * speed,
    rot: 0,
    frames: 0,
    // El bumerán gira más de prisa que un plátano: es lo que se lee como bumerán desde lejos.
    spin: boomerang ? BOOMERANG_SPIN : SPIN,
    // Con qué especial salió, y se guarda porque el botón puede haber cambiado de selección para
    // cuando el bumerán vuelva: medido, devolvía la carga al especial que el botón tenía puesto
    // entonces y el bumerán se quedaba sin la suya.
    special: special ? state.special[state.turn] : null,
    // What was thrown, not what the player has selected by the time it lands.
    kind: grenade ? 'grenade' : (ball ? 'ball' : (boomerang ? 'boomerang' : 'banana')),
    ball: ball ? ball.id : null,
    r: ball ? ball.r : 0,
    gravityScale: ball ? ball.gravity : 1,
    // A grenade is on a clock rather than on a collision course. A banana carries
    // a zero here and never reads it.
    fuse: grenade ? GRENADE_FUSE_FRAMES : 0,
    bounces: 0,
    windScale: ball
      ? ball.wind
      : (special && special.windScale !== undefined ? special.windScale : 1),
    craterScale: ball
      ? ball.crater
      : grenade
        ? (special.craterScale || GRENADE_CRATER_SCALE)
        : ((special && special.craterScale) || 1),
  };

  if (ball) showToast(t('toast.ball', { icon: ball.icon, name: t(`ball.${ball.id}`) }));

  if (special) spendSpecial(state.special[state.turn]);

  fireBtn.disabled = true;

  record('throw');
  initAudio();

  if (grenade) {
    // The line is most of the joke, so it goes up as the thing leaves the hand.
    say(state.turn, t('bubble.fireInTheHole'));
    playGrenade();
  } else {
    playThrow();
  }

  // Called in with the shot rather than instead of it: the paint is for the shot
  // after this one, which is how a UAV actually works.
  if (special && special.drone) callDrone();

  // The bang wakes up anyone who nodded off waiting for this shot.
  wakeGorillas();

  // The render loop is already running and carries the banana from here.
}

/**
 * One frame of a leap: steps the jump, and lets the banana go at the top of it.
 * Driven by the render loop next to advanceProjectile, for the same reason.
 */
export function advanceLeap() {
  const l = state.leap;
  if (!l) return;

  l.frames += 1;

  if (l.frames === LEAP_RELEASE_FRAME) launchLeapThrow();
  if (leapDone()) endLeap();
}

/**
 * The leap throw: a banana solved to arrive exactly on the target that was fixed
 * before the jump, thrown from the top of it, with the wind taken out of it.
 *
 * Three things stop it being a guaranteed kill, and all three are the design
 * rather than its bugs. The target does not track — the rival's pose keeps
 * moving, antics move the hitbox, and this banana is in the air for over a second,
 * so a head that ducks is a miss. The flight is a real arc, so a tower in the way
 * stops it like any other shot. And there is one of them per match.
 */
function launchLeapThrow() {
  const l = state.leap;
  if (!l || !l.target) return;

  const p = state.players[l.idx];
  if (!p || !p.alive) return;

  const start = gorillaFistPos(p, l.idx);
  const n = LEAP_FLIGHT_FRAMES;
  const g = mods().gravity;

  // Aimed, not placed. He is in the middle of a jump when he lets go, and an exact
  // solve would make this a near-certain kill — measured, not assumed. The scatter
  // is spread evenly over the disc so the edge is no rarer than the middle.
  const spread = LEAP_SCATTER;
  const bearing = Math.random() * Math.PI * 2;
  const reach = Math.sqrt(Math.random()) * spread;
  const x = l.target.x + Math.cos(bearing) * reach;
  const y = l.target.y + Math.sin(bearing) * reach;

  state.projectile = {
    x: start.x,
    y: start.y,
    vx: (x - start.x) / n,
    // The step-before-the-move integration means the fall over n frames is
    // g*n*(n+1)/2 rather than the g*n*n/2 a continuous model gives. Solving for
    // the real one is what lands it on the point instead of a frame past it.
    vy: (y - start.y - g * n * (n + 1) / 2) / n,
    rot: 0,
    frames: 0,
    kind: 'banana',
    fuse: 0,
    bounces: 0,
    // Thrown hard from the air: the wind does not get a say in it.
    windScale: 0,
    craterScale: 1,
  };

  fireBtn.disabled = true;
  record('throw');
  playThrow();
  say(l.idx, t('bubble.leap'));
  wakeGorillas();
}

/** Says what a crate held, and refreshes the button that can now spend it. */
export function announceLoot(loot) {
  // Y dice de quién es, que ha dejado de ser siempre el jugador: con el regalo en carrera, la CPU
  // también lo abre, y "tuyo también la ronda siguiente" sobre una carga que se ha llevado ella es
  // una mentira que se nota en la ronda siguiente.
  const mine = loot.idx === humanSide();

  // La vida extra no lleva nombre de especial: es un premio de otro tipo y se anuncia como tal.
  if (loot.kind === 'life') {
    showToast(
      t(mine ? 'toast.crateLife' : 'toast.crateLifeTheirs', mine ? {} : { who: playerName(loot.idx) }),
      { kind: 'reward' },
    );
    return;
  }

  const name = t(`special.${loot.id}.name`);

  showToast(
    t(mine ? 'toast.crateSpecial' : 'toast.crateTheirs', mine ? { name } : { name, who: playerName(loot.idx) }),
    { kind: 'reward' },
  );

  // Heard as well as read: a present that arrives silently is a present a player can
  // miss entirely, which is what this was.
  playReward();
  updateSpecialButton();
}

/**
 * Uses one charge of the thrower's own selection, and clears it once the last
 * one is gone. Takes no index: the thrower is always whose turn it is.
 */
/**
 * Spends one charge of a special, and empties the slot it came out of.
 *
 * It takes the id rather than reading a slot, because there are two slots now and the
 * caller is the one that knows which of them it is spending.
 */
function spendSpecial(id) {
  if (!id) return;

  // Counted for the achievements, and this is the one place a special is ever spent: an ult by
  // being fired, a defence by being braced with. `spentInMatch` is what "won without spending a
  // thing" reads at the end of the match.
  state.spentInMatch = true;
  record('special', { id });

  const idx = state.turn;
  state.specialUses[idx][id] -= 1;

  if (state.specialUses[idx][id] > 0) return;

  delete state.specialUses[idx][id];
  if (state.special[idx] === id) state.special[idx] = null;
  if (state.guard[idx] === id) state.guard[idx] = null;
}

/** Credits the round to whoever just landed a direct hit. */
function endRound(winner = state.turn) {
  clearPendingTimers();
  state.shield = null;

  // Restart the wait clock. Without this the gorilla that was nodded off, woken
  // by the shot and then left standing while the loser explodes would drop
  // straight back to sleep through the celebration.
  state.turnStartedAt = state.clock;

  // The nod. Said while the loser is still in the air, and timed to be gone
  // before the next round's city lands on top of it.
  say(winner, t('bubble.headshot'), 1700);

  // And the caption over the blast. Set here because this is the one place a
  // death resolves — a banana, a grenade or the thrower's own blast all end up in
  // this function — which is also what stops the three of them drifting apart.
  state.wasted = { at: state.clock };

  state.score[winner]++;
  state.matchWins[winner]++;
  updateLabels();
  // El lado va explícito porque el ganador no es siempre el del turno: un tiro que se cae al
  // vacío le da la ronda al otro, y esa ronda no es del que tiene el mando.
  record('roundWon', { side: winner });

  // And the match is over when the winner reaches the length it was built with — which is no length
  // at all when the setup asked for a match with no end to it. That one cannot be reached, so the
  // round below is always built and the match runs until somebody leaves it.
  const target = state.targetWins;
  if (target > 0 && state.matchWins[winner] >= target) {
    state.matchOver = true;
    // There is nothing to go back to once it has been won: the card is the end.
    state.matchLive = false;
    fireBtn.disabled = true;
    record('matchWon', {
      side: winner,
      clean: state.matchWins[1 - winner] === 0,
      vsCpu: state.cpuOn && winner === 0,
      bare: !state.spentInMatch,
      // Where it was won, since the two ways of playing a person have an achievement each.
      room: !!state.room,
      code: !!state.byCode && !state.room,
    });
    // The winner's dance, and only in the original's. It is a figure off a PC
  // speaker, and the modern modes are already being played off the stage by their
  // own band — which keeps going, rather than being cut for seven beeps.
  if (isClassicMode()) playVictory();

    // The local leaderboard. Deliberately local: there is no server, and the
    // menu card for ONLINE says as much rather than pretending.
    addResult({
      winner: playerName(winner),
      wins: state.matchWins[winner],
      losses: state.matchWins[1 - winner],
      mode: activeMode().id,
      date: new Date().toISOString().slice(0, 10),
    });

    // And the card. Held back a moment so the winner's dance is not covered up
    // by a menu the instant the last banana lands.
    setTimeout(() => {
      if (!state.matchOver) return;
      renderResults();
      showScreen('results');
    }, ROUND_DELAY_MS);

    // And to the shared board, if there is one and the player has said who they
    // are. Fire and forget: a finished match must never wait on the network.
    //
    // Con el nick de esta máquina y las rondas de ESTE lado. La fila se firmaba con el nick
    // local y los números del ganador: si ganaba la CPU 3-0, tu nombre subía a la tabla con
    // tres victorias que no habías ganado, y en una partida por la red subían las dos — la
    // tuya con las del rival y la suya con las suyas. Solo publica la partida que gana el
    // lado humano, que es lo que el nick y el marcador pueden firmar de verdad.
    //
    // Y el lado es uno FIJO, que no es lo que devuelve `humanSide()`: en un duelo de dos
    // personas al mismo teclado esa función devuelve el turno, y el turno ya ha cambiado
    // cuando la jugada se resuelve — medido, un dron que mató al jugador 1 con el turno
    // puesto en el 1 daba `humanSide() === 1` y una victoria del jugador 0 no se publicaba
    // nunca. El nick es de un lado solo: el asiento en una partida por la red, y el 0 contra
    // la máquina y en el mismo teclado.
    const me = state.online ? state.seat : 0;
    const who = savedName();
    if (who && winner === me) {
      publish({
        name: who,
        wins: state.matchWins[me],
        losses: state.matchWins[1 - me],
        mode: activeMode().id,
      });
    }
  } else {
    // Two swings starting after the impact fits inside the round delay with room
    // to spare, so nothing is still playing when the next level begins.
    if (isClassicMode()) playVictory(2);
    state.roundTimer = setTimeout(() => {
      state.roundTimer = null;
      state.round++;
      makeCity();
      // A new city is a new state entire, and the other end has not seen it: the round it
      // is standing in is the one that just ended.
      syncOnline();
    }, ROUND_DELAY_MS);
  }
}

/**
 * One frame of banana flight. Driven by the render loop for as long as a banana
 * is in the air; painting is the loop's job, not this function's.
 */
export function advanceProjectile() {
  const p = state.projectile;
  if (!p) return;

  const fromX = p.x;
  const fromY = p.y;

  // De vuelta pesa menos: ver BOOMERANG_RETURN_GRAVITY.
  const vuelve = p.kind === 'boomerang' && p.frames >= BOOMERANG_OUT_FRAMES;
  p.vy += mods().gravity * (p.gravityScale ?? 1) * (vuelve ? BOOMERANG_RETURN_GRAVITY : 1);
  // Y el viento tampoco le empuja en la vuelta, por lo mismo que la gravedad: lo que regresa a una
  // mano no es un proyectil a merced del aire, es un objeto que viene de vuelta. Y no es sólo
  // criterio: con el viento puesto, un muelle tan flojo como `BOOMERANG_PULL` tiene su punto de
  // equilibrio a `viento / pull` del objetivo —del orden del propio radio de captura—, así que el
  // bumerán se quedaba a un pelo de la mano y había que esperar al seguro: medido, volvían dos de
  // cada cinco tiros y los otros tres morían en el tope de `BOOMERANG_HOME_FRAMES`.
  p.vx += state.wind * WIND_DRIFT * (vuelve ? 0 : (p.windScale ?? 1));

  // El bumerán, y toda su física es esta: sale libre y, pasados unos fotogramas, la fuerza apunta
  // al que lo tiró. Con el muelle flojo sale, se abre y regresa solo, y el alcance lo marca el
  // tiempo — así que un rival lejos se queda fuera y uno cerca se lleva el golpe a la ida. El
  // viento y la gravedad le siguen valiendo: es un tiro, no un boomerang de dibujos.
  if (p.kind === 'boomerang' && p.frames >= BOOMERANG_OUT_FRAMES) {
    const owner = state.players[state.turn];
    if (owner && owner.alive) {
      // Y a la mano: una distancia al gorila, no la caja del cuerpo. Volver y no cogerlo no es un
      // tiro difícil, es un tiro roto: se anula, se devuelve la carga y el turno se queda donde
      // estaba, que es toda la recompensa del bumerán.
      if (Math.hypot(owner.x - p.x, owner.y - 26 - p.y) <= BOOMERANG_CATCH_R) {
        state.projectile = null;
        const id = p.special || state.special[state.turn];
        state.specialUses[state.turn][id] = Math.min(CHARGE_CAP, (state.specialUses[state.turn][id] || 0) + 1);
        updateSpecialButton();
        record('boomerang');
        playReward();
        say(state.turn, t('bubble.boomerangBack'), 1500);
        return;
      }

      // Y si lleva demasiado volviendo, se acabó: el turno pasa y la carga se pierde, como el tiro
      // que es. Es el seguro de `BOOMERANG_HOME_FRAMES`, y existe para que esto no se quede colgado
      // nunca — que era lo que pasaba, con el bumerán orbitando al dueño para siempre.
      if (p.frames >= BOOMERANG_OUT_FRAMES + BOOMERANG_HOME_FRAMES) {
        state.projectile = null;
        record('miss');
        say(state.turn, t('bubble.boomerangLost'), 1500);
        passTurn();
        return;
      }

      // El objetivo no es la mano sino un punto por ENCIMA de ella, que va bajando hasta ser la
      // mano misma: el regreso va por el aire, por encima de los tejados del camino, en vez de en
      // línea recta a través de la ciudad. Ver BOOMERANG_HOME_LIFT.
      //
      // Y ese punto TIENE que llegar a ser la mano, que es lo que no hacía: con la altura atada
      // sólo a la distancia horizontal, el bumerán se quedaba orbitando alrededor del objetivo alto
      // —a 130 px sobre la mano se está fuera del radio de captura por mucho que se esté quieto—
      // y el tiro moría en el tope del seguro. Medido con viento cero en la misma ciudad: volvían
      // dos de cada seis. Ahora la altura se apaga sola con los fotogramas de vuelta, así que el
      // arco sirve para salir de entre los edificios y el final del viaje es la mano.
      const vueltaFrames = p.frames - BOOMERANG_OUT_FRAMES;
      const arco = Math.max(0, 1 - vueltaFrames / BOOMERANG_HOME_ARC_FRAMES);
      const lift = Math.min(BOOMERANG_HOME_LIFT, Math.abs(owner.x - p.x) * BOOMERANG_HOME_SLOPE) * arco;
      p.vx += (owner.x - p.x) * BOOMERANG_PULL;
      p.vy += ((owner.y - 26 - lift) - p.y) * BOOMERANG_PULL;

      // Y el aire lo frena. Sin esto lo que vuelve no es el bumerán sino una órbita: ver
      // BOOMERANG_DRAG, que es donde está la cuenta y la medida.
      p.vx *= BOOMERANG_DRAG;
      p.vy *= BOOMERANG_DRAG;
    }
  }

  p.x += p.vx;
  p.y += p.vy;
  p.rot = (p.rot || 0) + (p.spin ?? SPIN);
  p.frames = (p.frames || 0) + 1;

  // The thrower is checked too — a banana that comes back down on its own
  // gorilla counts, as it did in the original — but only once it has cleared the
  // fist, because it starts inside the throwing arm's own hitbox.
  const armed = p.frames > SELF_HIT_GRACE_FRAMES;

  // Sweep the whole gap rather than testing only where the banana arrived. A
  // single point test per frame both misses a thin arm the banana flew straight
  // through and lets it come to rest a full step short of what it hit — which
  // reads on screen as the explosion going off in mid-air, next to the gorilla.
  const travel = Math.hypot(p.x - fromX, p.y - fromY);
  const steps = Math.max(1, Math.ceil(travel / HIT_SWEEP_PX));
  let victim = -1;
  let blocked = null;
  let bounced = null;

  // Only ever the defender's, and never the thrower's own: a shield must not stop
  // the banana of the gorilla holding it, and a spring must not catch its own.
  // Nothing protects anybody in a mode without specials. None of these can be armed
  // there, so this is belt and braces — but it makes the rule true *here* rather
  // than true because nothing has gone wrong yet.
  const propsOn = mods().specials;
  const raised = propsOn && state.shield && state.shield.idx !== state.turn ? state.shield : null;
  const shieldRect = raised ? gorillaShieldRect(state.players[raised.idx], raised.idx) : null;
  const planted = propsOn && state.spring && state.spring.idx !== state.turn ? state.spring : null;
  const springRect = planted ? gorillaSpringRect(state.players[planted.idx], planted.idx) : null;
  let crate = propsOn ? crateRect() : null;

  for (let s = 1; s <= steps && victim < 0 && !blocked && !bounced; s++) {
    const t = s / steps;
    const x = fromX + (p.x - fromX) * t;
    const y = fromY + (p.y - fromY) * t;

    // Inside the same sweep as the gorillas and before them, so whichever of the
    // three the banana meets first along its path is the one that counts. A
    // shield stands in front and so does a spring, so in practice it is one of
    // those.
    // A crate is broken open and the shot carries on. The point of it is the loot
    // rather than the shot, and nothing stops the banana that found one.
    if (crate
      && x >= crate.x && x <= crate.x + crate.w
      && y >= crate.y && y <= crate.y + crate.h) {
      const loot = collectCrate();
      crate = null;
      if (loot) announceLoot(loot);
    }

    if (springRect
      && x >= springRect.x && x <= springRect.x + springRect.w
      && y >= springRect.y && y <= springRect.y + springRect.h) {
      bounced = { x, y };
      break;
    }

    if (shieldRect
      && x >= shieldRect.x && x <= shieldRect.x + shieldRect.w
      && y >= shieldRect.y && y <= shieldRect.y + shieldRect.h) {
      blocked = { x, y };
      break;
    }

    state.players.forEach((pl, i) => {
      if (!pl.alive || victim >= 0 || blocked) return;
      if (i === state.turn && !armed) return;
      // El plátano que acaba de devolver un muelle no puede volver a dar al que lo devolvió: el
      // rebote sale de DENTRO de su caja, así que sin esto se mataba con su propio muelle. Deja de
      // ser suyo en cuanto sale de él, que es lo único que se mira aquí.
      if (i === p.springOwner) {
        if (!hitsGorilla(x, y, pl, i)) p.springOwner = null;
        return;
      }
      if (hitsGorilla(x, y, pl, i)) {
        // Un escudo levantado protege por completo, y eso es lo que significa: no es un trozo de
        // tabla en el camino, es que a ese gorila no le da este tiro. Se para justo donde le
        // habría dado, así que el plátano no explota y no abre cráter — y una trayectoria que
        // caía muy vertical y pasaba por encima del rectángulo ya no le hace nada. Lo pidió el
        // jugador con estas palabras: «el escudo protege por completo».
        if (raised && i === raised.idx) {
          blocked = { x, y };
          return;
        }

        // Y el muelle hace lo contrario con ese mismo tiro: no lo para, lo devuelve. La prueba
        // va aquí, y no contra el rectángulo del muelle, porque el muelle está DEBAJO del gorila
        // —es la peana en la que baila— y su rectángulo cae entero dentro de la caja de golpe del
        // propio gorila: medido, era imposible que un plátano lo tocara antes que a él, y por eso
        // rebotaba 4 trayectorias de 496 (el 0,8 %). Peor: el paseo lo eleva y lo dejaba MÁS
        // fácil de dar, 33 impactos con muelle contra 28 sin él. Un muelle que no devuelve nada
        // no es una defensa, es un adorno.
        if (planted && i === planted.idx) {
          bounced = { x, y };
          return;
        }

        victim = i;
        // Stop it exactly where it met the gorilla, not at the frame boundary.
        p.x = x;
        p.y = y;
      }
    });
  }

  if (bounced) {
    // Thrown back the way it came, and it keeps going: this is the one catch that
    // does not end a shot. Everything after this point is ordinary flight, which
    // is what lets it go all the way back and hit whoever threw it.
    p.x = bounced.x;
    p.y = bounced.y;
    p.vx = -p.vx;
    p.vy = -Math.abs(p.vy) - SPRING_KICK;
    state.spring = null;
    // Y sale de dentro del que lo plantó, así que ese lado deja de ser suyo mientras lo hace —
    // ver el barrido de arriba, que es donde se pregunta.
    p.springOwner = planted.idx;
    playClink();
    return;
  }

  if (blocked) {
    // A shield is a wall, not a smaller target: the banana stops on it, nothing
    // explodes, and the shield is spent. It lasted the one shot it went up for.
    state.projectile = null;
    state.shield = null;
    record('miss');
    playClink();
    passTurn();
    return;
  }

  if (victim >= 0) {
    const selfHit = victim === state.turn;
    state.projectile = null;
    const gorilla = state.players[victim];

    // The mushroom is spent by the shot it saves him from, and only by that. A
    // hit that would have ended the round lands, and the round carries on.
    //
    // Y se dice, que era el fallo de bulto que tenía: el que la llevaba salvaba la ronda sin que
    // nada en pantalla lo dijera —ni cartel, ni burbuja, sólo un clin—, así que lo que se veía era
    // «el rival ha fallado» y la seta se esfumaba sola. La vida extra ya se anunciaba así; esto es
    // lo mismo para la defensa que promete lo mismo.
    if (!selfHit && mods().specials && state.mushroom && state.mushroom.idx === victim) {
      state.mushroom = null;
      record('miss');
      playClink();
      say(victim, t('bubble.mushroomSaved'), 1700);
      passTurn();
      return;
    }

    // Y si no hay seta, la vida extra: aguanta el golpe que mata y la ronda sigue. Vale también
    // para el tiro que se da uno a sí mismo, que es un golpe mortal como cualquier otro.
    if (spendLife(victim)) {
      passTurn();
      return;
    }

    gorilla.alive = false;
    record(selfHit ? 'selfHit' : 'hit');
    // The blast goes off on the gorilla itself, not on the banana that found it.
    nuke(gorilla.x, gorilla.y);
    endRound(selfHit ? 1 - state.turn : state.turn);
    return;
  }

  // A grenade is on a clock rather than on a collision course: it goes off
  // wherever it has got to. That is the whole difference — with a banana you are
  // aiming at the gorilla, with a grenade at the roof it is standing on.
  if (p.kind === 'grenade') {
    p.fuse -= 1;
    if (p.fuse <= 0) return detonate(p);
  }

  // The moon is scenery: not a wall, and not a thing a banana can hit either. It is a
  // circle painted in the background. Crossing its face still counts for the lucky-shot
  // achievement, and that is the whole of it — the shot carries on and leaves nothing.
  //
  // It used to throw twelve grey particles off its face so the player could watch himself
  // clip it. Measured, that is the only thing up there that reacts to a banana at all: no
  // collision, and yet a shower of dust where the moon is. And the moon's band is 59 to
  // 135 while the score panels are 16 to 64, so the shower landed on the scoreboard —
  // reported as a banana colliding with the moon and with the markers, which is what a
  // mark thrown off a background circle looks like from the player's chair.
  //
  // Where the moon is comes from moon.js, the same place the painter asks, and it moves,
  // so this has to ask every frame.
  const moon = moonAt();

  if (moon && !p.moonPassed && Math.hypot(p.x - moon.x, p.y - moon.y) < moon.r) {
    p.moonPassed = true;
    record('moonHit');
  }

  // or a building block?
  //
  // El bumerán que VUELVE no choca, y esta línea ya estuvo así una vez, luego se le puso la
  // colisión y ahora vuelve a su sitio. La historia, que es la lección:
  //
  // 1. Al principio volvía en línea recta a la mano y se eximía de toda colisión: se estrellaba
  //    contra la primera torre que tuviera debajo de todas formas, porque la recta pasaba por
  //    dentro de los edificios.
  // 2. Se le puso un arco para que el regreso fuera por el aire (`_HOME_LIFT`, `_HOME_ARC_FRAMES`)
  //    y, con el arco puesto, se le devolvió la colisión: el razonamiento era que ya no hacía falta
  //    eximirlo, porque ahora subía. Y es verdad que sube.
  // 3. Pero sube 130 px sobre la mano, y las torres del centro de la ciudad llegan a **520**: en
  //    cuanto el dueño tiene una torre entre él y su bumerán, el regreso la cruza. Y cuando la
  //    cruza, el bumerán muere contra ella, y morir contra una pared es `record('miss')` y
  //    **`passTurn()`** — o sea: el jugador ve volver el bumerán a su mano y acto seguido pasa el
  //    turno, porque se ha estrellado a dos píxeles de cogerlo. Reportado así: «cuando pillas el
  //    bumerán de vuelta, pasas turno».
  //
  // Entre un bumerán que atraviesa un edificio en la vuelta —cruzando por arriba, que es lo que
  // dibuja— y un tiro que se pierde cuando ya había vuelto, lo segundo es peor: la vuelta a la mano
  // es la promesa del bumerán (`ver más abajo: «volver y no cogerlo no es un tiro difícil, es un
  // tiro roto»`). Así que la vuelta se salta los edificios, igual que ya se salta el suelo, y la
  // **ida sigue chocando como cualquier tiro**: medido, a 15° y 30° el bumerán se estrella en la
  // ida a los 29 y 40 fotogramas, antes de que la vuelta empiece, y eso se queda.
  const hitB = vuelve
    ? null
    : state.buildings.find(b => p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h);
  if (hitB) {
    // Sample the building's own pixels: fully transparent means it flew
    // through a window or an earlier crater.
    const lx = Math.floor(p.x - hitB.x);
    const ly = Math.floor(p.y - hitB.y);
    const solid = hitB.bctx.getImageData(clamp(lx, 0, hitB.w - 1), clamp(ly, 0, hitB.h - 1), 1, 1).data[3] > 10;

    if (solid) {
      // A grenade does not go off on contact, it bounces. Having it detonate
      // against the first wall it meets would make it a banana with a different
      // picture, and the fuse is the point.
      if (p.kind === 'grenade') {
        bounceGrenade(p, hitB, fromX, fromY);
        return;
      }

      const px = p.x;
      const py = p.y;
      // The special multiplies what was thrown; the mode multiplies the whole
      // arsenal. Demolition mode is what happens when the second one is big.
      const r = (CRATER_RADIUS + rand(0, CRATER_JITTER)) * (p.craterScale ?? 1) * mods().craterScale;
      crater(hitB, px, py, r);
      state.projectile = null;
      record('miss');
      passTurn();
      explode(px, py);

      return;
    }
  }

  if (p.x < 0 || p.x > W || p.y > H) {
    // Y el bumerán que vuelve no muere en el suelo: vuelve a la mano, y el suelo está justo por
    // debajo de ella. Medido, se estrellaba a 84 px de su dueño —el tiro se perdía a un paso, que
    // es peor que fallarlo—. Sí se pierde si se va por debajo del campo o fuera por los lados, que
    // eso ya no es volver.
    const volviendo = p.kind === 'boomerang' && p.frames >= BOOMERANG_OUT_FRAMES
      && p.y < H + 90 && p.x > -120 && p.x < W + 120;
    if (!volviendo) {
      state.projectile = null;
      record('miss');
      passTurn();
      return;
    }
  }
}

/**
 * Gasta una vida extra del que iba a morir, si le queda alguna.
 *
 * El último recurso, detrás del escudo y de la seta: esas dos son defensas que se eligen y se
 * gastan, y esto es un colchón que uno se encuentra por el camino. Cuando salva, la ronda sigue
 * y el turno pasa, que es exactamente lo que hace la seta.
 */
function spendLife(idx) {
  if (!(state.lives[idx] > 0)) return false;

  state.lives[idx] -= 1;
  // Es un acierto y no un fallo: la vida le ha librado de morir, pero el golpe le ha dado, y el
  // contador de aciertos y la racha son de eso.
  record('hit');
  playClink();
  say(idx, t('bubble.extraLife'), 1700);
  return true;
}

/**
 * Reflects a grenade off the building it has just met. Which face it hit is
 * worked out by comparing where it was a frame ago with where it is now:
 * whichever direction it came in from is the direction it goes back out.
 */
function bounceGrenade(p, b, fromX, fromY) {
  const fromSide = fromX < b.x || fromX > b.x + b.w;
  const fromTopOrBottom = fromY < b.y || fromY > b.y + b.h;

  // Put it back where it was, so the next frame does not simply find it inside
  // the same wall and bounce it again from the wrong side.
  p.x = fromX;
  p.y = fromY;

  if (Math.hypot(p.vx, p.vy) < GRENADE_REST_SPEED) {
    // Too slow to be a bounce: it has rattled itself to a stop up on a rooftop.
    // Leave it lying there, and cut the flight fuse down — it is not going to
    // roll anywhere, so the only thing the rest of that fuse buys is a wait.
    p.vx = 0;
    p.vy = 0;
    p.fuse = Math.min(p.fuse, GRENADE_SETTLE_FUSE);
    return;
  }

  if (fromSide && !fromTopOrBottom) {
    p.vx = -p.vx * GRENADE_BOUNCE;
  } else if (fromTopOrBottom && !fromSide) {
    p.vy = -p.vy * GRENADE_BOUNCE;
  } else {
    // A corner, or a point that was somehow inside on both axes. Send it straight
    // back the way it came rather than letting it stick in the wall.
    p.vx = -p.vx * GRENADE_BOUNCE;
    p.vy = -p.vy * GRENADE_BOUNCE;
  }

  p.bounces = (p.bounces || 0) + 1;
  if (state.audioCtx) playClink();
}

/**
 * A grenade going off. Unlike a banana it does not have to touch anybody: the
 * blast reaches GRENADE_BLAST_R and anything inside that dies. That is the whole
 * point of it — you lob it at the roof they are standing on.
 */
function detonate(p) {
  const px = p.x;
  const py = p.y;
  // The special multiplies what was thrown; the mode multiplies the whole arsenal.
  const scale = (p.craterScale ?? 1) * mods().craterScale;

  state.projectile = null;

  // Carve the building it went off in, if it went off in one. An airburst has
  // nothing underneath it to hole.
  const b = state.buildings.find(q => px >= q.x && px <= q.x + q.w && py >= q.y && py <= q.y + q.h);
  if (b) crater(b, px, py, (CRATER_RADIUS + rand(0, CRATER_JITTER)) * scale);

  const caught = [0, 1].filter(i => state.players[i].alive
    && Math.hypot(state.players[i].x - px, state.players[i].y - py) <= GRENADE_BLAST_R);

  // Y la seta vale aquí igual que contra un plátano: la carta promete "se lleva el golpe que te
  // mata", y una explosión es exactamente eso. Hasta ahora solo la consultaba el barrido del
  // proyectil, así que una granada o una oveja se la llevaban por delante y el que la llevaba
  // moría con la seta puesta — dos de los ataques ignoraban la única defensa que promete eso.
  // Se consume, y el que la lleva sale del radio: si era el único, la explosión se queda en un
  // susto y el turno pasa como con cualquier fallo.
  const kept = mods().specials && state.mushroom ? caught.indexOf(state.mushroom.idx) : -1;
  if (kept >= 0) {
    state.mushroom = null;
    caught.splice(kept, 1);
    say(kept, t('bubble.mushroomSaved'), 1700);
  }

  // Y las vidas extra, una por cada uno que iba a morir y solo si le queda: los que se salvan
  // salen de la lista, y un lado con dos vidas aguanta dos explosiones.
  for (let i = caught.length - 1; i >= 0; i -= 1) {
    if (spendLife(caught[i])) caught.splice(i, 1);
  }

  if (!caught.length) {
    record('miss');
    if (kept >= 0) playClink();
    explode(px, py);
    passTurn();
    return;
  }

  // Its own thrower counts, exactly as it does with a banana. Lobbing a grenade
  // at the roof you are standing on is a mistake the game should let you make.
  const victim = state.players[caught[0]];
  const vx = victim.x;
  const vy = victim.y;

  caught.forEach(i => {
    state.players[i].alive = false;
  });

  const self = caught.includes(state.turn);
  record(self ? 'selfHit' : 'hit');
  nuke(vx, vy);
  endRound(self ? 1 - state.turn : state.turn);
}

/**
 * Launches the sheep. It does not fly: it hops, and the turn is spent the moment it leaves.
 *
 * It is the only thing in the game that keeps acting after the hand has left the sliders.
 * Every other shot resolves in one flight; this one is a small animal crossing a city, one
 * big hop at a time, deciding for itself how far it gets — which is exactly why it can be
 * wasted (off the end of the map), wasted differently (stuck against a tower) or decisive.
 */
function launchSheep(idx) {
  const start = gorillaFistPos(state.players[idx], idx);
  const dir = facingOf(idx);

  state.sheep = {
    idx,
    x: start.x,
    y: start.y,
    vx: SHEEP_SPEED * dir,
    vy: 0,
    dir,
    hops: 0,
    // Born in the fist, so it starts out inside its own thrower's hitbox. See advanceSheep().
    armed: false,
    startAt: state.clock,
  };

  spendSpecial(state.special[idx]);
  fireBtn.disabled = true;
  say(idx, t('bubble.sheep'), 900);
  record('throw');
  initAudio();
  playBaa();
  wakeGorillas();
}

/**
 * One frame of the sheep's walk, from the render loop.
 *
 * The hop is an ordinary parabola and the rooftops are what it lands on: a hop that comes
 * down inside a building lands on that building's roof rather than in it, and that is what
 * makes it walk along the skyline instead of dying at the first low wall.
 *
 * A hop that cannot go on is one blocked sideways — the step forward would put it inside the
 * building in front, so the step is dropped and the hop goes up and down on the spot. That is
 * the sheep stuck, bleating at a tower, and it is not a bug: it is what the fuse is for. The
 * bleat per hop is the clock the player hears, and nothing stalls while it happens.
 */
export function advanceSheep() {
  const s = state.sheep;
  if (!s) return;

  const inBuilding = (x, y) => state.buildings.find(b => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h);

  // Off the ends of the map without having hit anybody. It walks out of the level and that is
  // that — a wasted turn rather than a lost one, which is what was asked for.
  if (s.x < -SHEEP_MARGIN || s.x > W + SHEEP_MARGIN) {
    state.sheep = null;
    passTurn();
    return;
  }

  // The fuse. A bomb with legs does not stand about, so a sheep that has got nowhere goes off
  // exactly where it stands.
  if (state.clock - s.startAt > SHEEP_FUSE_MS) {
    sheepBoom(s.x, s.y);
    return;
  }

  // Either of them, its owner included — but the owner only once the animal has left its hand.
  //
  // The sheep is spawned at the thrower's fist, which is inside the thrower's own hitbox, so
  // testing the owner from the first frame blew it up where it stood: measured, it died on frame
  // zero, never moved a pixel, and handed the round to the other side. It arms the moment it is
  // clear of the hand and stays armed: from then on the owner is a target like anybody else, and
  // a sheep that comes home is the oldest joke in the genre. detonate() already works out whether
  // the one who copped it is the one who threw it, and gives the round to the other side.
  const touching = i => state.players[i] && state.players[i].alive
    && hitsGorilla(s.x, s.y, state.players[i], 0);

  if (touching(1 - s.idx)) {
    sheepBoom(s.x, s.y);
    return;
  }

  const inHand = touching(s.idx);
  if (!s.armed && !inHand) s.armed = true;
  if (s.armed && inHand) {
    sheepBoom(s.x, s.y);
    return;
  }

  // Forward, and a wall turns it round rather than stopping it.
  //
  // The rule is the one that was asked for: a sheep does not climb. It walks the skyline down —
  // a step down is a hop, a step up of more than a lip is a wall — and a wall is not a dead
  // end, it is a change of direction. So a sheep that has hopped into a hollow is not stuck in
  // the way a stopped one would be: it walks back and forth inside it, bleating once a hop,
  // until it finds a side that is level or lower, or until the fuse finds it first. That is
  // what "it stays there until it can get out" means, and it is a great deal better to watch
  // than an animal standing still with its nose against a wall.
  const nx = s.x + s.vx;
  // The wall is the FACE it is walking at, and the step only crosses a face on one frame of the
  // hundred a building is wide. Testing the building the step lands in instead turned the sheep
  // round on every frame it spent over a taller one's span, and reversing every frame is a
  // shiver, not a walk: measured, 99 changes of direction in twelve seconds and 205 px of ground.
  const ahead = state.buildings.find(b => nx >= b.x && nx <= b.x + b.w);
  const face = ahead ? (s.vx > 0 ? ahead.x : ahead.x + ahead.w) : 0;
  const crossesFace = !!ahead
    && (s.vx > 0 ? s.x <= face && nx > face : s.x >= face && nx < face);
  // And it is a wall only while the sheep is below the top of it. The same subtraction says both
  // "there is a roof sticking up in front of me" and "I am a hundred and seventy pixels over
  // that roof", which is not a wall at all but a hop going over it — the reason an earlier
  // version, which measured the step from the roof it was standing on, walked the whole city
  // without turning once. A lip of a few pixels is a step, not a wall.
  if (crossesFace && s.y - ahead.y > SHEEP_STEP_UP) {
    s.vx = -s.vx;
    s.dir = -s.dir;
  } else {
    s.x = nx;
  }

  s.vy += mods().gravity;
  s.y += s.vy;

  if (s.vy > 0) {
    const roof = inBuilding(s.x, s.y);
    if (roof) {
      s.y = roof.y;
      s.vy = -SHEEP_HOP_VY;
      s.hops += 1;
      playBaa();
      state.shake = Math.max(state.shake, 2.5);
    }
  }
}

/** The sheep going off: the grenade's own bang and damage, at the sheep's own size. */
function sheepBoom(x, y) {
  state.sheep = null;
  detonate({ x, y, craterScale: SHEEP_CRATER_SCALE });
}

/**
 * Sends the drone over, painting the other side with it. Called in with the shot
 * rather than instead of it, so the paint is for the shot after this one — which
 * is how a UAV actually works.
 */
function callDrone() {
  const dir = facingOf(state.turn);

  state.drone = { x: dir > 0 ? -46 : W + 46, y: DRONE_Y, dir };
  state.paintedFor = 1 - state.turn;
  state.paintedUntil = state.clock + UAV_PAINT_MS;

  playDrone();
  // Being painted is the other side's news, and it is something they should be
  // told rather than have to notice.
  say(1 - state.turn, t('bubble.uavOnline'), 2000);
}

/**
 * One frame of the drone's pass, driven by the render loop. The paint outlives
 * the pass on purpose: the drone is long gone by the time you take the shot it
 * was called in for.
 */
export function advanceDrone() {
  const d = state.drone;

  if (d) {
    d.x += DRONE_SPEED * d.dir;
    if (d.x < -70 || d.x > W + 70) state.drone = null;
  }

  if (state.paintedFor >= 0 && state.clock >= state.paintedUntil) {
    state.paintedFor = -1;
    state.paintedUntil = 0;
  }

  // Fuera del tablero no pasa nada de esto: la bomba se resuelve con la partida delante, igual
  // que el reloj del turno y la caja que cae.
  if (state.screen !== 'game' || matchOnHold() || state.matchOver) return;

  if (!state.bomb) {
    // Se suelta al pasar sobre el pintado, que es a quien ha venido a buscar.
    const target = state.paintedFor >= 0 ? state.players[state.paintedFor] : null;
    if (d && target && target.alive && Math.abs(d.x - target.x) <= DRONE_DROP_X) {
      state.bomb = {
        // Sobre la vertical del que va pintado, no sobre la del dron: la carga viene a por él, y
        // doce píxeles de desfase al soltarla eran una cabeza rozada y no un impacto.
        x: Math.round(target.x), y: d.y + 8, vy: DRONE_BOMB_VY,
        victim: state.paintedFor, from: 1 - state.paintedFor,
      };
      playGrenade();
    }
    return;
  }

  const bomb = state.bomb;
  bomb.vy += mods().gravity;
  bomb.y += bomb.vy;

  // Explota al darle a quien venía a buscar, al tejado que tiene debajo, o al suelo del campo.
  const victim = state.players[bomb.victim];
  const struck = victim && victim.alive && hitsGorilla(bomb.x, bomb.y, victim, bomb.victim);
  const roof = state.buildings.some(b => bomb.x >= b.x && bomb.x <= b.x + b.w
    && bomb.y >= b.y && bomb.y <= b.y + b.h);

  if (struck || roof || bomb.y > H) bombBlast(bomb);
}

/**
 * La bomba del dron estallando.
 *
 * La misma cuenta que la granada —cráter donde cae y un radio que mata— pero con el dueño
 * apuntado en la propia carga: la bomba se resuelve cuando al dron le ha dado tiempo a llegar, y
 * para entonces el turno hace rato que es del otro, así que preguntar de quién es el turno daría
 * el ganador al revés.
 *
 * El escudo NO está aquí, y es a propósito: es un muro, y un muro para lo que vuela contra él —un
 * plátano, una pelota, un bumerán—, no una explosión que estalla encima. La seta sí, porque su
 * carta promete llevarse el golpe que te mata, sea plátano o metralla. Antes estaba al revés: el
 * escudo paraba esta bomba y no la granada, que son la misma cosa — medido, con el escudo puesto
 * se sobrevivía a esta y se moría con una granada a treinta píxeles.
 */
function bombBlast(bomb) {
  state.bomb = null;

  const { x, y } = bomb;
  const onTarget = state.players[bomb.victim];

  if (onTarget && onTarget.alive && mods().specials
    && state.mushroom && state.mushroom.idx === bomb.victim) {
    state.mushroom = null;
    record('miss');
    playClink();
    say(bomb.victim, t('bubble.mushroomSaved'), 1700);
    explode(x, y);
    return;
  }

  const scale = DRONE_BOMB_CRATER * mods().craterScale;
  const b = state.buildings.find(q => x >= q.x && x <= q.x + q.w && y >= q.y && y <= q.y + q.h);
  if (b) crater(b, x, y, (CRATER_RADIUS + rand(0, CRATER_JITTER)) * scale);

  const caught = [0, 1].filter(i => {
    const pl = state.players[i];
    if (!pl.alive) return false;
    // El radio de la granada se mide desde los pies y aquí no sirve: esta bomba cae sobre la
    // cabeza, que está 38 px más arriba, así que medido desde los pies se quedaba corto justo
    // cuando había dado de lleno. Vale el cuerpo o el radio.
    return hitsGorilla(x, y, pl, i) || Math.hypot(pl.x - x, pl.y - y) <= DRONE_BLAST_R;
  });

  // Y la vida extra vale aquí como en cualquier otro golpe mortal.
  for (let i = caught.length - 1; i >= 0; i -= 1) {
    if (spendLife(caught[i])) caught.splice(i, 1);
  }

  if (!caught.length) {
    record('miss');
    explode(x, y);
    return;
  }

  const self = caught.includes(bomb.from);
  const victim = state.players[caught[0]];
  const vx = victim.x;
  const vy = victim.y;

  caught.forEach(i => {
    state.players[i].alive = false;
  });

  record(self ? 'selfHit' : 'hit');
  nuke(vx, vy);
  endRound(self ? 1 - bomb.from : bomb.from);
}

/**
 * What the CPU brings to this turn, if anything.
 *
 * Deliberately plain, and deliberately not optimal. It arms a protection when it
 * has just been hit and still holds one, and otherwise reaches for an ult now
 * and then. Handing it a kit was about the match being fair; making it play that
 * kit perfectly would only move the unfairness to the other side — a CPU that
 * always answered a near miss with a shield is nobody's idea of a good evening.
 */
function chooseCpuSpecial() {
  if (!mods().specials) return null;

  const mine = state.specialUses[1] || {};
  const held = kind => specialsOfKind(kind).filter(id => (mine[id] || 0) > 0);
  const ults = held('ult');
  const guards = held('guard');
  const drawFrom = list => list[Math.floor(Math.random() * list.length)];

  // Behind this round, so it defends. The one piece of judgement in here.
  if (state.score[0] > state.score[1] && guards.length && Math.random() < CPU_GUARD_CHANCE) {
    return drawFrom(guards);
  }

  if (ults.length && Math.random() < CPU_ULT_CHANCE) return drawFrom(ults);
  if (guards.length && Math.random() < CPU_GUARD_CHANCE) return drawFrom(guards);

  return null;
}

/**
 * The turn clock, called every frame.
 *
 * Running out loses the turn, but only while the player is in a position to throw:
 * not mid-flight, not mid-leap, not while the other side is thinking, and not while
 * the match is off screen.
 *
 * The equipment menu does NOT hold it. Choosing what to throw is part of the turn,
 * and a clock that stops behind that menu is a clock that can be stalled for as long
 * as the player likes; picking a card closes the panel, so the choice itself stays
 * quick, and reading all eleven descriptions now costs what it costs.
 *
 * Whether it is held is asked here, afresh, from the screen alone, rather than taken
 * and given back by whoever opens something. It was a pair of calls before, one from
 * the panel machinery and one from the screen change, and a pause lost its grip on
 * the clock the moment the equipment menu was closed on top of it: the pause was
 * charged to the turn, and as the clock is not redrawn while a match is off screen,
 * it said nothing about it. One flag with two owners is one owner too many.
 *
 * Firing for them would reward walking away from the keyboard, which is the opposite
 * of what a turn clock is for.
 */
export function advanceTurnClock() {
  if (state.matchOver) return;

  // La partida no se le cobra a nadie cuando no se puede jugar: o la pantalla no es la de juego, o
  // `matchOnHold` dice que sí lo es pero hay algo delante —el aviso del móvil de pie, o un panel:
  // logros, personajes, clasificaciones o el zurrón—. Esa pregunta vive en un solo sitio a
  // propósito, porque ahora la hace también el bucle: un panel que paraba el reloj y dejaba caer la
  // caja era lo peor de los dos mundos, y medido —con el zurrón abierto la caja bajó 70 px y la CPU
  // se la llevó mientras el reloj seguía clavado en 13—.
  const held = state.screen !== 'game' || matchOnHold();

  if (held) {
    if (!state.turnHoldSince) state.turnHoldSince = state.clock;
  } else if (state.turnHoldSince) {
    // Given back in one go, and never more than this turn has lived: a marker left
    // up by a previous turn is not this turn's time to hand back.
    const since = Math.max(state.turnHoldSince, state.turnStartedAt);
    state.turnStartedAt += state.clock - since;
    state.turnHoldSince = 0;
  }

  // Redrawn every frame, and while it is held as well, so the number on screen is
  // the turn's own countdown rather than the last one anybody happened to write.
  // Not from updateLabels: that runs when a turn changes and when a slider moves,
  // so a clock drawn from it sits at 15 and then jumps.
  updateTurnClock();

  if (held) return;
  if (state.projectile || state.leap || state.sheep) return;
  if (state.cpuOn && state.turn === 1) return;

  const left = secondsLeft();

  if (left <= 0) {
    // Se acabó el tiempo, y eso se dice — pero se le dice a quien lo ha perdido.
    //
    // En online las dos máquinas cuentan el mismo turno, así que sin esta condición el aviso sonaba
    // también en el extremo que estaba esperando: el rival oía el «se te acabó» de un turno que no
    // era el suyo, y el suyo de verdad sonaba dos veces (una en cada máquina). `machineHasTurn` es
    // la misma pregunta que ya usa el tic —«le toca a quien no está aquí»— así que los dos avisos
    // van juntos: el de los últimos segundos y el del final.
    //
    // El turno pasa igual, que es lo que no se puede condicionar: el reloj es de la partida.
    state.turnTickAt = 0;
    if (!machineHasTurn()) playTimeUp();
    passTurn();
    return;
  }

  // Y antes de que se acabe, se avisa. Hasta ahora eso sólo lo decía el reloj puesto en rojo, que
  // es un aviso mudo para quien está mirando el campo y no la esquina: un tic por segundo entero de
  // los últimos `TURN_RUSH_SECONDS`, y el último más agudo.
  //
  // Sólo suena si el turno es de alguien que lo puede perder, y esa pregunta ya está escrita:
  // `machineHasTurn` es «le toca a quien no está en esta máquina», así que el turno de la CPU no
  // tica —al suyo no se le cobra el tiempo, la línea de arriba sale antes, y avisarle sería avisar
  // de algo que no pasa—. En hotseat tica siempre, porque los dos están aquí.
  if (!machineHasTurn()) {
    const marca = Math.ceil(left);
    if (marca <= TURN_RUSH_SECONDS && marca !== state.turnTickAt) {
      state.turnTickAt = marca;
      playTurnTick(marca <= 1);
    }
  }
}

/**
 * A shot that lands, found by throwing it.
 *
 * The same arithmetic the banana flies with — gravity every frame, the wind pushing sideways
 * every frame, no drag — so a throw can be simulated exactly without touching the game. A
 * coarse sweep of the two dials finds plenty that hit: measured, 44 to 61 clear solutions in
 * every one of twelve cities. The difficulty is what happens to the one it picks afterwards.
 */
function solveCpuShot() {
  const me = state.players[1];
  const target = state.players[0];
  if (!me || !target || !me.alive || !target.alive) return null;

  const hits = [];
  for (let deg = 20; deg <= 80; deg += 2) {
    for (let pow = 25; pow <= 100; pow += 3) {
      if (cpuShotLands(deg, pow, me, target)) hits.push({ deg, pow });
    }
  }

  return hits.length ? hits[Math.floor(Math.random() * hits.length)] : null;
}

/** One simulated throw: true when it reaches the rival before a wall or the edge. */
function cpuShotLands(deg, pow, me, target) {
  const dir = facingOf(1);
  const a = deg * Math.PI / 180;
  const speed = pow * SPEED_SCALE;
  const start = gorillaFistPos(me, 1);

  let x = start.x;
  let y = start.y;
  let vx = Math.cos(a) * speed * dir;
  let vy = -Math.sin(a) * speed;

  for (let f = 0; f < CPU_SOLVE_FRAMES; f++) {
    vy += mods().gravity;
    vx += state.wind * WIND_DRIFT;
    x += vx;
    y += vy;

    if (Math.abs(x - target.x) < 60 && Math.abs(y - target.y) < 60 && hitsGorilla(x, y, target, 0)) return true;
    if (state.buildings.some(b => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h)) return false;
    if (x < -40 || x > W + 40 || y > H) return false;
  }

  return false;
}

/** If it is the CPU's turn, work out a shot and fire it after a beat. */
export function maybeCpuTurn() {
  cancelCpuTurn();
  if (!state.cpuOn || state.turn !== 1 || state.matchOver) return;

  // Lo que el humano tenga preparado en los sliders se archiva ANTES de que la CPU escriba los suyos,
  // y esto es lo que arregla el «me cambia el ángulo y la fuerza mientras preparo el tiro».
  //
  // La CPU apunta escribiendo su ángulo y su potencia en el único par de sliders que hay —así se lee
  // un tiro— y al acabar el turno se le devuelven al humano los números del archivo. El problema era
  // cuándo se escribía ese archivo: en `fireShot`, o sea con el ÚLTIMO TIRO del humano. Entre su tiro
  // y el de la CPU pasan segundos de sobra para ir preparando el siguiente, y en esos segundos el
  // jugador movía los mandos para que la CPU se los devolviera a los de antes. Archivarlo aquí —una
  // vez por turno de la CPU, y tanto si el turno acaba en tiro como si acaba en defensa, porque los
  // dos pasan por aquí— hace que lo que se devuelva sea exactamente lo que había puesto él.
  saveAim(prepareSide());

  // During the CPU's turn the human must not pull the trigger on its behalf.
  fireBtn.disabled = true;

  // What it is bringing, settled before the numbers are dialled: a CPU that
  // changed its mind mid-think could arm a shield after aiming for a throw. One thing
  // at a time, in the slot that matches it, so its turn never has two answers in it.
  state.special[1] = null;
  state.guard[1] = null;
  const cpuPick = chooseCpuSpecial();
  if (cpuPick) (SPECIALS[cpuPick].guard ? state.guard : state.special)[1] = cpuPick;

  const level = CPU_LEVELS.find(l => l.id === savedCpuLevel()) || CPU_LEVELS[1];
  const shot = solveCpuShot();
  // Finding nothing is not a reason to do nothing: a flat lob at the middle of its range is
  // a turn spent rather than a turn skipped.
  const base = shot || { deg: 45, pow: 70 };

  const a = clamp(base.deg + (Math.random() * 2 - 1) * level.scatterDeg, 20, 160);
  const pow = clamp(base.pow + (Math.random() * 2 - 1) * level.scatterPower, 25, 100);

  state.cpuTimer = setTimeout(() => {
    state.cpuTimer = null;
    // The match may have been reset, or the CPU switched off, while thinking.
    if (!state.cpuOn || state.turn !== 1 || state.matchOver || state.projectile) return;
    angleSlider.value = Math.round(a);
    powerSlider.value = Math.round(pow);
    updateLabels();
    // A defence is a turn of its own for the CPU as well: it braces instead of
    // throwing, and its slot being full is what says it decided on one.
    if (state.guard[1]) brace();
    else {
      fireShot();
      // And the sliders go back to the human's own numbers in the same frame they were borrowed
      // in. The CPU aims by writing its angle and power on the one pair of sliders there is —
      // that is how a shot is read — and leaving them there puts its numbers under the human's
      // hands for the whole flight of its banana: measured, that is the second and a half this
      // was reported as "the controls move when it is not my turn". Its own aim was filed by
      // fireShot before anything moved, so nothing of its is lost by putting them back.
      loadAim(prepareSide());
    }
  }, level.thinkMs);
}
